import { useState, useEffect, createContext, useContext, ReactNode, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshAdminStatus: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Client-side admin check (RLS protected)
  const checkAdminRoleClientSide = async (userId: string): Promise<boolean> => {
    if (!userId) return false;

    try {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle();
      
      return !!data;
    } catch (err) {
      console.error("Client-side admin check failed:", err);
      return false;
    }
  };

  // Server-side admin verification via edge function
  const verifyAdminViaEdgeFunction = async (): Promise<boolean> => {
    try {
      const { data, error } = await supabase.functions.invoke("admin-role-management", {
        body: { action: "check" },
      });

      if (error) {
        console.error("Edge function admin check failed:", error);
        return false;
      }

      return data?.isAdmin === true;
    } catch (err) {
      console.error("Admin verification error:", err);
      return false;
    }
  };

  // Public method to refresh admin status
  const refreshAdminStatus = useCallback(async (): Promise<boolean> => {
    if (!user?.id) {
      setIsAdmin(false);
      return false;
    }

    // Try edge function first, fallback to client-side
    let adminStatus = await verifyAdminViaEdgeFunction();
    if (!adminStatus) {
      adminStatus = await checkAdminRoleClientSide(user.id);
    }
    
    setIsAdmin(adminStatus);
    return adminStatus;
  }, [user?.id]);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // Get existing session
        const { data: { session: existingSession } } = await supabase.auth.getSession();
        
        if (!mounted) return;

        if (existingSession?.user) {
          setSession(existingSession);
          setUser(existingSession.user);
          
          // Check admin status in background - don't block loading
          verifyAdminViaEdgeFunction().then((adminStatus) => {
            if (mounted) {
              if (adminStatus) {
                setIsAdmin(true);
              } else {
                // Fallback to client-side check
                checkAdminRoleClientSide(existingSession.user.id).then((clientAdminStatus) => {
                  if (mounted) setIsAdmin(clientAdminStatus);
                });
              }
            }
          }).catch(() => {
            // Fallback to client-side check on error
            checkAdminRoleClientSide(existingSession.user.id).then((clientAdminStatus) => {
              if (mounted) setIsAdmin(clientAdminStatus);
            });
          });
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (!mounted) return;

        setSession(newSession);
        setUser(newSession?.user ?? null);
        setIsLoading(false);
        
        if (newSession?.user) {
          // Check admin status in background using setTimeout to avoid deadlock
          setTimeout(() => {
            verifyAdminViaEdgeFunction().then((adminStatus) => {
              if (mounted) {
                if (adminStatus) {
                  setIsAdmin(true);
                } else {
                  checkAdminRoleClientSide(newSession.user.id).then((clientAdminStatus) => {
                    if (mounted) setIsAdmin(clientAdminStatus);
                  });
                }
              }
            }).catch(() => {
              checkAdminRoleClientSide(newSession.user.id).then((clientAdminStatus) => {
                if (mounted) setIsAdmin(clientAdminStatus);
              });
            });
          }, 0);
        } else {
          setIsAdmin(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectUrl },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        session, 
        isAdmin, 
        isLoading, 
        signIn, 
        signUp, 
        signOut, 
        refreshAdminStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
